import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import { formatDate } from '../../helpers/UtilsFunctions';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import { AlertDialog } from '../Common';
import '../../styles/loading-animations.css';

const THEME_GREEN = '#009391';
const BRAND_TEAL = '#26C3C0';
const PAGE_SIZES = [5, 10, 25, 50];

const getTextFieldSx = (mode) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: mode === 'dark' ? '#444' : '#ddd',
    },
    '&:hover fieldset': {
      borderColor: BRAND_TEAL,
      boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_TEAL,
      boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
    },
  },
  '& .MuiInputLabel-root': {
    color: mode === 'dark' ? '#999' : '#666',
    fontSize: '14px',
  },
});

const formatStatusLabel = (value) => {
  if (!value) return '-';
  const normalized = String(value).toLowerCase();
  if (normalized === 'active') return 'Active';
  if (normalized === 'inactive' || normalized === 'deactive') return 'Deactive';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusBadgeStyle = (value, mode) => {
  const base = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid transparent',
  };
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
      color: '#059669',
      borderColor: mode === 'dark' ? 'rgba(16,185,129,0.4)' : '#a7f3d0',
    };
  }
  if (normalized === 'inactive' || normalized === 'deactive') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2',
      color: '#dc2626',
      borderColor: mode === 'dark' ? 'rgba(239,68,68,0.4)' : '#fecaca',
    };
  }
  return {
    ...base,
    background: mode === 'dark' ? '#1f2937' : '#f3f4f6',
    color: mode === 'dark' ? '#e5e7eb' : '#374151',
    borderColor: mode === 'dark' ? '#374151' : '#e5e7eb',
  };
};

const extractDocuments = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.documents)) return response.documents;
  if (Array.isArray(response?.data?.documents)) return response.data.documents;
  return [];
};

const normalizeDocument = (doc, index) => {
  const fileName =
    doc?.original_name ||
    doc?.file_name ||
    doc?.document_name ||
    doc?.name ||
    `Document ${index + 1}`;
  const title = doc?.title || doc?.document_title || fileName;
  const id = doc?.id || doc?.document_id || doc?.uuid || `${title}-${index}`;
  const uploadedAt =
    doc?.created_at || doc?.uploaded_at || doc?.created_on || doc?.createdAt || null;

  return { id, title, fileName, uploadedAt, raw: doc };
};

const getDocumentId = (doc) => doc?.raw?.id || doc?.raw?.document_id || doc?.raw?.uuid || doc?.id;

const SiteDetail = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteId } = useParams();

  const [site, setSite] = useState(() => location.state?.site || null);
  const [isLoading, setIsLoading] = useState(!location.state?.site);
  const [error, setError] = useState('');

  const [documents, setDocuments] = useState([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [docsPage, setDocsPage] = useState(0);
  const [docsPageSize, setDocsPageSize] = useState(10);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const [deleteDocDialog, setDeleteDocDialog] = useState({ isOpen: false, doc: null });
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  useEffect(() => {
    const loadSite = async () => {
      if (site) return;
      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getSitesList({ showLoading: false });
        const list = Array.isArray(response?.data) ? response.data : [];
        const match = list.find((item) => String(item.id) === String(siteId));
        if (match) {
          setSite(match);
        } else {
          setError('Site not found.');
        }
      } catch (apiError) {
        setError(apiError?.message || 'Failed to load site details.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSite();
  }, [site, siteId]);

  const fetchDocuments = useCallback(async () => {
    if (!siteId) return;
    try {
      setIsDocumentsLoading(true);
      setDocumentsError('');
      const response = await dataAPI.getSiteDocuments(siteId, { showLoading: false });
      const docs = extractDocuments(response).map((item, index) => normalizeDocument(item, index));
      setDocuments(docs);
    } catch (apiError) {
      setDocuments([]);
      setDocumentsError(apiError?.message || 'Unable to load documents right now.');
    } finally {
      setIsDocumentsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    setDocsPage(0);
  }, [documents.length, docsPageSize]);

  const paginatedDocuments = documents.slice(
    docsPage * docsPageSize,
    docsPage * docsPageSize + docsPageSize
  );

  const handleOpenUpload = () => {
    setUploadTitle('');
    setUploadFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsUploadOpen(true);
  };

  const handleCloseUpload = () => {
    if (isUploading) return;
    setIsUploadOpen(false);
    setUploadTitle('');
    setUploadFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setUploadFile(file);
    if (uploadError) setUploadError('');
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0] || null;
    if (file) {
      setUploadFile(file);
      if (uploadError) setUploadError('');
    }
  };

  const canUpload = uploadTitle.trim() && uploadFile && !isUploading;

  const handleUploadDocument = async () => {
    if (!uploadTitle.trim()) {
      setUploadError('Document title is required.');
      return;
    }
    if (!uploadFile) {
      setUploadError('Please select a document file.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('title', uploadTitle.trim());
      formData.append('document', uploadFile);

      const response = await dataAPI.uploadSiteDocument(siteId, formData, { showLoading: false });
      if (response?.status === 'success') {
        await fetchDocuments();
        handleCloseUpload();
      } else {
        setUploadError(response?.message || 'Failed to upload document.');
      }
    } catch (apiError) {
      setUploadError(apiError?.message || 'Failed to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    const documentId = getDocumentId(doc);
    if (!documentId) {
      alert('Unable to download this document right now.');
      return;
    }

    try {
      const response = await dataAPI.downloadSiteDocument(siteId, documentId, { showLoading: false });
      const downloadUrl =
        response?.data?.download_url || response?.download_url || response?.data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Download link is not available at the moment.');
      }
    } catch (apiError) {
      alert(apiError?.message || 'Failed to download the document.');
    }
  };

  const handleDeleteDocumentClick = (doc) => {
    setDeleteDocDialog({ isOpen: true, doc });
  };

  const handleDeleteDocumentConfirm = async () => {
    const doc = deleteDocDialog.doc;
    const documentId = getDocumentId(doc);
    if (!documentId || isDeletingDoc) return;

    setIsDeletingDoc(true);
    try {
      const response = await dataAPI.deleteSiteDocument(siteId, documentId, { showLoading: false });
      if (response?.status === 'success') {
        setDocuments((prev) => prev.filter((item) => getDocumentId(item) !== documentId));
        setDeleteDocDialog({ isOpen: false, doc: null });
      } else {
        alert(response?.message || 'Failed to delete document.');
      }
    } catch (apiError) {
      alert(apiError?.message || 'Failed to delete document.');
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const cardStyle = {
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.35)' : '0 6px 18px rgba(0,0,0,0.08)',
  };

  const detailFields = site
    ? [
        { label: 'Site Name', value: site.site_name },
        { label: 'Site Address', value: site.site_address },
        {
          label: 'Status',
          value: (
            <span style={getStatusBadgeStyle(site.status, mode)}>
              {formatStatusLabel(site.status)}
            </span>
          ),
        },
        { label: 'Created By', value: site.created_by_username },
        { label: 'Created At', value: site.created_at ? formatDate(site.created_at) : '-' },
      ]
    : [];

  return (
    <div
      style={{
        padding: '24px',
        minHeight: '100vh',
        background: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        color: mode === 'dark' ? '#fff' : '#111827',
        fontFamily: 'Urbanist, Arial, sans-serif',
      }}
    >
      <button
        type="button"
        onClick={() => navigate(APP_URLS.SITES)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${mode === 'dark' ? '#333' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '8px 14px',
          background: 'transparent',
          color: mode === 'dark' ? '#fff' : '#111827',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        <ArrowBackIcon style={{ fontSize: 18 }} />
        Back to Sites
      </button>

      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 220px)',
            marginTop: '24px',
          }}
        >
          <img
            src={AppIconBlue}
            alt="Loading"
            className="pulse-animation-icon"
            style={{ width: 46, height: 46 }}
          />
        </div>
      ) : error ? (
        <div
          style={{
            marginTop: '24px',
            border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fecaca'}`,
            borderRadius: '12px',
            padding: '16px',
            color: mode === 'dark' ? '#fecaca' : '#b91c1c',
          }}
        >
          {error}
        </div>
      ) : !site ? (
        <div style={{ marginTop: '24px', ...cardStyle }}>Site not found.</div>
      ) : (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          {/* Site details section */}
          <section style={cardStyle}>
            <h2 style={{ margin: '0 0 20px', fontSize: '22px', fontWeight: 700 }}>Site Details</h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px',
              }}
            >
              {detailFields.map((field) => (
                <div key={field.label}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                      marginBottom: '6px',
                    }}
                  >
                    {field.label}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>{field.value || '-'}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Documents section */}
          <section style={cardStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Documents</h2>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUpload}
                sx={{
                  backgroundColor: THEME_GREEN,
                  borderRadius: '10px',
                  textTransform: 'none',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#007b79' },
                }}
              >
                Upload Document
              </Button>
            </div>

            {documentsError ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#fecaca' : '#b91c1c',
                  fontWeight: 600,
                }}
              >
                {documentsError}
              </div>
            ) : isDocumentsLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px',
                }}
              >
                <img
                  src={AppIconBlue}
                  alt="Loading"
                  className="pulse-animation-icon"
                  style={{ width: 40, height: 40 }}
                />
              </div>
            ) : documents.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                }}
              >
                No documents uploaded yet.
              </div>
            ) : (
              <Paper
                sx={{
                  border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                }}
              >
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thStyle(mode)}>Title</TableCell>
                        <TableCell sx={thStyle(mode)}>File Name</TableCell>
                        <TableCell sx={thStyle(mode)}>Uploaded At</TableCell>
                        <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDocuments.map((doc) => (
                        <TableRow
                          key={doc.id}
                          sx={{
                            '& td': {
                              borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                            },
                          }}
                        >
                          <TableCell sx={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <InsertDriveFileIcon
                                sx={{ fontSize: 18, color: THEME_GREEN, flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: 600 }}>{doc.title}</span>
                            </div>
                          </TableCell>
                          <TableCell sx={tdStyle}>{doc.fileName}</TableCell>
                          <TableCell sx={tdStyle}>
                            {doc.uploadedAt ? formatDate(doc.uploadedAt) : '-'}
                          </TableCell>
                          <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', gap: '4px' }}>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadDocument(doc)}
                                  sx={{ color: THEME_GREEN }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocumentClick(doc)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={documents.length}
                  page={docsPage}
                  onPageChange={(_, newPage) => setDocsPage(newPage)}
                  rowsPerPage={docsPageSize}
                  onRowsPerPageChange={(event) => {
                    setDocsPageSize(parseInt(event.target.value, 10));
                    setDocsPage(0);
                  }}
                  rowsPerPageOptions={PAGE_SIZES}
                  sx={{
                    color: mode === 'dark' ? '#d0d5dd' : '#344054',
                    borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                    backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                  }}
                />
              </Paper>
            )}
          </section>
        </div>
      )}

      {/* Upload document modal — matches Documents module */}
      {isUploadOpen && (
        <>
          <div
            role="presentation"
            onClick={handleCloseUpload}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 1100,
              backdropFilter: 'blur(2px)',
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '480px',
              maxWidth: '92vw',
              maxHeight: '90vh',
              overflowY: 'auto',
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
              zIndex: 1101,
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px 12px',
                borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                }}
              >
                Upload Document
              </h2>
              <button
                type="button"
                onClick={handleCloseUpload}
                disabled={isUploading}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  color: mode === 'dark' ? '#FFFFFF' : '#666666',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CloseIcon />
              </button>
            </div>

            <div style={{ padding: '20px 24px 24px' }}>
              {uploadError && (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: mode === 'dark' ? 'rgba(244,67,54,0.15)' : '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '8px',
                    color: mode === 'dark' ? '#ff8a8a' : '#d32f2f',
                    fontSize: '14px',
                  }}
                >
                  {uploadError}
                </div>
              )}

              <TextField
                size="small"
                required
                fullWidth
                label="Document Title"
                value={uploadTitle}
                onChange={(e) => {
                  setUploadTitle(e.target.value);
                  if (uploadError) setUploadError('');
                }}
                disabled={isUploading}
                sx={{ ...getTextFieldSx(mode), mb: 2 }}
              />

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={handleFileDrop}
                style={{
                  border: `2px dashed ${mode === 'dark' ? '#444' : '#ddd'}`,
                  borderRadius: '8px',
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                  position: 'relative',
                  marginBottom: '20px',
                }}
                onMouseEnter={(e) => {
                  if (!isUploading) e.currentTarget.style.borderColor = BRAND_TEAL;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = mode === 'dark' ? '#444' : '#ddd';
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                  }}
                />
                <CloudUploadIcon
                  style={{ fontSize: 42, color: BRAND_TEAL, marginBottom: 12 }}
                />
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: BRAND_TEAL,
                    marginBottom: 6,
                  }}
                >
                  Click to Upload Document
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: mode === 'dark' ? '#999' : '#666',
                  }}
                >
                  Supported: .pdf, .doc, .docx, .xls, .xlsx, .png, .jpg, .jpeg, .gif, .txt
                  <br />
                  Drag and drop a file here or click to browse
                </div>
                {uploadFile && (
                  <div
                    style={{
                      marginTop: 12,
                      fontSize: '14px',
                      color: BRAND_TEAL,
                      fontWeight: 600,
                    }}
                  >
                    Selected: {uploadFile.name}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={handleCloseUpload}
                  disabled={isUploading}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: 'transparent',
                    color: mode === 'dark' ? '#FFFFFF' : '#374151',
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUploadDocument}
                  disabled={!canUpload}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: canUpload ? BRAND_TEAL : mode === 'dark' ? '#444' : '#9ca3af',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: canUpload ? 'pointer' : 'not-allowed',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    boxShadow: canUpload ? '0 2px 8px rgba(38, 195, 192, 0.35)' : 'none',
                    transition: 'background-color 0.2s ease',
                    opacity: isUploading ? 0.85 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (canUpload) e.currentTarget.style.backgroundColor = '#22B0AD';
                  }}
                  onMouseLeave={(e) => {
                    if (canUpload) e.currentTarget.style.backgroundColor = BRAND_TEAL;
                  }}
                >
                  {isUploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <AlertDialog
        isOpen={deleteDocDialog.isOpen}
        onClose={() => !isDeletingDoc && setDeleteDocDialog({ isOpen: false, doc: null })}
        onConfirm={handleDeleteDocumentConfirm}
        type="delete"
        title="Delete Document"
        message="Are you sure you want to delete this document?"
        itemName={deleteDocDialog.doc?.title || ''}
        confirmText={isDeletingDoc ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
      />
    </div>
  );
};

const thStyle = (mode) => ({
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: mode === 'dark' ? '#111111' : '#f9fafb',
  color: mode === 'dark' ? '#ffffff' : '#111827',
});

const tdStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
};

export default SiteDetail;
